import React, { useLayoutEffect, useState, useRef } from "react"
import * as am5 from "@amcharts/amcharts5"
import * as am5xy from "@amcharts/amcharts5/xy"
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated"
import * as am5exporting from "@amcharts/amcharts5/plugins/exporting"

// Function
import { getRandomId } from "./functions"

import ExcelJS from "exceljs"
import { saveAs } from "file-saver"

// Interface
interface HeatMapRiskProps {
  width?: string
  height?: string
  idChart?: string
  dataChart?: any
  xAxisValue?: any
  yAxisValue?: any
  colorSetTheme?: string[]
  primaryActionColor?: string
  enableExportMenu?: boolean
  widgetTitle?: string
  titleAxeX?: string
  titleAxeY?: string
  onSegmentClick?: (args: { preloadedItems: any[]; referentialId?: string }) => void
}

const HeatMapRisk: React.FC<HeatMapRiskProps> = ({
  width = "100%",
  height = "100%",
  idChart,
  dataChart = [],
  xAxisValue = [],
  yAxisValue = [],
  colorSetTheme,
  enableExportMenu = false,
  widgetTitle = "HeatMap Risk",
  titleAxeX = "",
  titleAxeY = "",
  primaryActionColor = "#383838",
  onSegmentClick,
  ...props
}) => {
  // State
  const [idChartState, setIdChartState] = useState(idChart ? idChart : getRandomId())
  // Refs
  const exportMenuRef = useRef<am5exporting.Exporting | null>(null)
  const rootRef = useRef<am5.Root | null>(null)
  const chartRef = useRef<am5xy.XYChart | null>(null)

  const exportToExcel = async () => {
    if (!chartRef.current) return

    type CellKey = `${string}|${string}`
    const dataItems = chartRef.current.series.getIndex(0).dataItems

    const grid: Record<CellKey, { value: number; colorHex: string }> = {}
    dataItems.forEach(di => {
      const x = di.get("categoryX") as string
      const y = di.get("categoryY") as string
      const value = (di.get("value") as number) || 0

      const settings = (di.dataContext as any).columnSettings
      let hex = "#FFFFFF"
      if (settings?.fill instanceof am5.Color) {
        hex = settings.fill.toCSSHex()
      } else if (typeof settings?.fill === "string") {
        hex = settings.fill
      }

      grid[`${x}|${y}` as CellKey] = { value, colorHex: hex }
    })

    const wb = new ExcelJS.Workbook()
    const ws = wb.addWorksheet("HeatMap")

    const xCats = xAxisValue.map(c => c.category as string)
    const xLabels = xAxisValue.map(c => c.label || (c.category as string))
    const yCats = yAxisValue.map(c => c.category as string)
    const yLabels = yAxisValue.map(c => c.label || (c.category as string))

    // Ligne 1 : Titre principal
    ws.addRow([widgetTitle])
    const titleRow = ws.getRow(1)
    titleRow.font = { bold: true, size: 14 }
    titleRow.alignment = { horizontal: "center" }
    ws.mergeCells(1, 1, 1, xLabels.length + 3)

    // Ligne 2 : Titre axe X fusionné à partir de colonne 3
    ws.addRow([null, null, titleAxeX])
    const axesTitleRow = ws.getRow(2)
    axesTitleRow.font = { bold: true, size: 12 }
    axesTitleRow.alignment = { horizontal: "center" }
    ws.mergeCells(2, 3, 2, xLabels.length + 2)

    // Ligne 3 : Labels X
    ws.getCell(3, 1).value = null
    const xHeaderRow = ws.getRow(3)
    xLabels
      .slice()
      .reverse()
      .forEach((label, i) => {
        const cell = xHeaderRow.getCell(i + 3)
        cell.value = label
        cell.font = { bold: true }
        cell.alignment = { horizontal: "center" }
      })

    // Lignes de données Y
    yLabels.forEach((yLabel, yIndex) => {
      const yCat = yCats[yIndex]
      const rowValues = [
        null,
        yLabel,
        ...xCats
          .slice()
          .reverse()
          .map(xCat => grid[`${xCat}|${yCat}`]?.value ?? 0),
      ]
      const rowNumber = yIndex + 4
      const row = ws.insertRow(rowNumber, rowValues)

      const yLabelCell = row.getCell(2)
      yLabelCell.font = { bold: true }
      yLabelCell.alignment = { horizontal: "center", vertical: "middle" }

      row.eachCell({ includeEmpty: true }, (cell, colIdx) => {
        if (colIdx <= 2) return
        const xCat = xCats.slice().reverse()[colIdx - 3]
        const { colorHex } = grid[`${xCat}|${yCat}`] || { colorHex: "#FFFFFF" }
        const argb = "FF" + colorHex.substring(1).toUpperCase()
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb },
        }
        cell.numFmt = "#,##0"
        cell.alignment = { horizontal: "center", vertical: "middle" }
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        }
      })
    })

    // Fusion verticale Axe Y (après ajout des lignes)
    const startRowY = 4
    const endRowY = yLabels.length + 3
    ws.mergeCells(startRowY, 1, endRowY, 1)
    const yAxisTitleCell = ws.getCell(startRowY, 1)
    yAxisTitleCell.value = titleAxeY
    yAxisTitleCell.font = { bold: true, size: 12 }
    yAxisTitleCell.alignment = {
      textRotation: 90,
      vertical: "middle",
      horizontal: "center",
      wrapText: true,
    }

    // Totaux par colonne (X) — ligne juste après les données
    const totalRowIndex = yLabels.length + 4
    const totalRow = ws.insertRow(totalRowIndex, [
      null,
      "",
      ...xCats
        .slice()
        .reverse()
        .map(xCat => yCats.reduce((sum, yCat) => sum + (grid[`${xCat}|${yCat}`]?.value ?? 0), 0)),
    ])
    totalRow.font = { bold: true }
    totalRow.alignment = { horizontal: "center", vertical: "middle" }

    // Totaux par ligne (Y) — colonne à droite
    const totalColIndex = 2 + xLabels.length + 1
    for (let i = 4; i < totalRowIndex; i++) {
      const row = ws.getRow(i)
      const yCat = yCats[i - 4]
      const total = xCats.reduce((sum, xCat) => sum + (grid[`${xCat}|${yCat}`]?.value ?? 0), 0)
      row.getCell(totalColIndex).value = total
      row.getCell(totalColIndex).font = { bold: true }
      row.getCell(totalColIndex).alignment = { horizontal: "center", vertical: "middle" }
    }

    // Ajustements visuels
    ws.columns.forEach((c, index) => {
      if (index === 0) {
        c.width = 20
      } else if (index === 1) {
        c.width = 15
      } else {
        c.width = 15
      }
    })
    ws.eachRow(r => (r.height = 20))

    const buf = await wb.xlsx.writeBuffer()
    saveAs(new Blob([buf]), `${widgetTitle}.xlsx`)
  }

  const disposeExportMenu = () => {
    if (exportMenuRef.current !== null) {
      exportMenuRef?.current?.get("menu")?.dispose()
      exportMenuRef.current.dispose()
      exportMenuRef.current = null
    }
  }
  const createExportMenu = (root: am5.Root) => {
    if (exportMenuRef.current || !enableExportMenu) return

    // 1) instancie l’exporting sans menu par défaut
    const exporting = am5exporting.Exporting.new(root, {
      menu: am5exporting.ExportingMenu.new(root, {
        items: [
          // images
          { type: "format", format: "png", label: "PNG" },
          { type: "format", format: "jpg", label: "JPG" },
          { type: "format", format: "pdf", label: "PDF" },
          // séparation
          { type: "separator" },
          {
            type: "custom",
            label: "Excel",
            callback: () => exportToExcel(),
          },
        ],
      }),
    })

    exportMenuRef.current = exporting
  }

  // const createExportMenu = (root: am5.Root) => {
  //   if (exportMenuRef.current === null && enableExportMenu) {
  //     const title = `${widgetTitle}_export`
  //     exportMenuRef.current = am5exporting.Exporting.new(root, {
  //       menu: am5exporting.ExportingMenu.new(root, {}),
  //       title: title,
  //       printOptions: {
  //         disabled: true,
  //       },
  //     })
  //   }
  // }

  const disposeChart = () => {
    if (rootRef.current !== null) {
      rootRef.current.dispose()
      rootRef.current = null
    }
    if (chartRef.current !== null) {
      chartRef.current.dispose()
      chartRef.current = null
    }
    disposeExportMenu()
  }

  // Initialize chart
  useLayoutEffect(() => {
    // Create root element
    let root = am5.Root.new(idChartState)
    rootRef.current = root

    // Remove amCharts logo
    root._logo?.dispose()

    // Set themes
    root.setThemes([am5themes_Animated.new(root)])

    // Create chart
    let chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: false,
        panY: false,
        wheelX: "none",
        wheelY: "none",
        paddingLeft: 0,
        paddingRight: 0,
        layout: root.verticalLayout,
      })
    )
    chartRef.current = chart

    // Create axes and their renderers
    let yRenderer = am5xy.AxisRendererY.new(root, {
      visible: false,
      minGridDistance: 20,
      inversed: true,
      minorGridEnabled: true,
    })

    yRenderer.grid.template.set("visible", false)

    let yAxis = chart.yAxes.push(
      am5xy.CategoryAxis.new(root, {
        renderer: yRenderer,
        categoryField: "category",
      })
    )

    let xRenderer = am5xy.AxisRendererX.new(root, {
      visible: false,
      minGridDistance: 30,
      inversed: true,
      minorGridEnabled: true,
    })

    xRenderer.grid.template.set("visible", false)

    let xAxis = chart.xAxes.push(
      am5xy.CategoryAxis.new(root, {
        renderer: xRenderer,
        categoryField: "category",
      })
    )

    // Set custom labels for the Y-axis
    yAxis.get("renderer").labels.template.adapters.add("text", (text, target) => {
      const dataItem = target.dataItem?.dataContext
      return dataItem?.label ? dataItem.label : dataItem?.category
    })

    // Set custom labels for the X-axis
    xAxis.get("renderer").labels.template.adapters.add("text", (text, target) => {
      const dataItem = target.dataItem?.dataContext
      return dataItem?.label ? dataItem.label : dataItem?.category
    })

    // Create series
    let series = chart.series.push(
      am5xy.ColumnSeries.new(root, {
        calculateAggregates: true,
        stroke: am5.color(0xffffff),
        clustered: false,
        xAxis: xAxis,
        yAxis: yAxis,
        categoryXField: "x",
        categoryYField: "y",
        valueField: "value",
      })
    )

    series.columns.template.setAll({
      tooltipText: "{value}",
      cursorOverStyle: "pointer",
      strokeOpacity: 1,
      strokeWidth: 2,
      cornerRadiusTL: 5,
      cornerRadiusTR: 5,
      cornerRadiusBL: 5,
      cornerRadiusBR: 5,
      width: am5.percent(100),
      height: am5.percent(100),
      templateField: "columnSettings",
    })

    series.columns.template.events.on("click", ev => {
      const ctx = ev?.target?.dataItem?.dataContext as any
      if (onSegmentClick) {
        const xLabel = (xAxisValue as any[])?.find(v => v.category === ctx?.x)?.label ?? ctx?.x ?? ""
        const yLabel = (yAxisValue as any[])?.find(v => v.category === ctx?.y)?.label ?? ctx?.y ?? ""
        onSegmentClick({
          preloadedItems: (ctx?.infoModal ?? []).map((item: any) => ({
            ...item,
            axeX: xLabel,
            axeY: yLabel,
            niveau: ctx?.value ?? 0,
          })),
          referentialId: ctx?.infoModal?.[0]?.info?.referentialId,
        })
      }
    })

    let circleTemplate = am5.Template.new({})

    // Add heat rule
    series.set("heatRules", [
      {
        target: circleTemplate,
        min: 15,
        max: 15,
        dataField: "value",
        key: "radius",
      },
    ])

    // Add bullets only for non-zero values
    series.bullets.push((root, series, dataItem) => {
      if (dataItem.dataContext.value > 0) {
        return am5.Bullet.new(root, {
          sprite: am5.Circle.new(
            root,
            {
              fill: am5.color(0x000000),
              fillOpacity: 0.5,
              strokeOpacity: 0,
            },
            circleTemplate
          ),
        })
      }
      return null
    })

    // Show labels only for values > 0
    series.bullets.push((root, series, dataItem) => {
      if (dataItem.dataContext.value > 0) {
        return am5.Bullet.new(root, {
          sprite: am5.Label.new(root, {
            fill: am5.color(0xffffff),
            populateText: true,
            centerX: am5.p50,
            centerY: am5.p50,
            fontSize: 10,
            text: "{value}",
          }),
        })
      }
      return null
    })

    // X-axis label
    xAxis.children.push(
      am5.Label.new(root, {
        text: titleAxeX,
        fontWeight: "bold",
        fontSize: 14,
        paddingTop: 15,
        centerX: am5.p50,
        x: am5.percent(50),
      })
    )

    // Y-axis label
    yAxis.children.insertIndex(
      0,
      am5.Label.new(root, {
        text: titleAxeY,
        fontWeight: "bold",
        fontSize: 14,
        rotation: -90,
        centerX: am5.p50,
        centerY: am5.p50,
        x: 0,
        y: am5.percent(50),
        paddingRight: 15,
      })
    )

    // Set data
    const roundedData = dataChart.map((data: any) => ({
      ...data,
      value: Math.ceil(data.value),
    }))

    series.data.setAll(roundedData)
    yAxis.data.setAll(yAxisValue)
    xAxis.data.setAll(xAxisValue)

    // Create export menu after chart is created
    createExportMenu(root)

    // Cleanup on unmount
    return () => {
      disposeChart()
    }
  }, [idChartState])

  // Update export menu when enableExportMenu changes
  useLayoutEffect(() => {
    if (rootRef.current !== null) {
      disposeExportMenu()
      createExportMenu(rootRef.current)
    }
  }, [enableExportMenu, widgetTitle])

  // Update data when dataChart changes
  useLayoutEffect(() => {
    if (chartRef.current && rootRef.current) {
      const series = chartRef.current.series.getIndex(0)
      if (series) {
        const roundedData = dataChart.map((data: any) => ({
          ...data,
          value: Math.ceil(data.value),
        }))
        series.data.setAll(roundedData)
      }
    }
  }, [dataChart])

  // Update axis data when xAxisValue or yAxisValue changes
  useLayoutEffect(() => {
    if (chartRef.current && rootRef.current) {
      const xAxis = chartRef.current.xAxes.getIndex(0)
      const yAxis = chartRef.current.yAxes.getIndex(0)

      if (xAxis) {
        xAxis.data.setAll(xAxisValue)
      }
      if (yAxis) {
        yAxis.data.setAll(yAxisValue)
      }
    }
  }, [xAxisValue, yAxisValue])

  return <div id={idChartState} style={{ width: width, height: height }} />
}

export default HeatMapRisk

